import { useCallback, useEffect, useRef, useState } from 'react'
import { timeSlotsBranchServices } from 'api/services'
import { IResTimeSLotApplication, IResTimeSlot } from 'api/services/timeslots-branches/types'
import { ShowNotification } from 'helpers/showNotice'
import { useSelector } from 'react-redux'
import { AppState } from 'store/store'
import moment from 'moment'
import { printData } from '../ReserveQueue/ReserveQueue'
import { generateQRBase64 } from 'helpers/generateQRBase64'
import { Button, TextField } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close';
import NumericTerminal from 'components/NumericTerminal/NumericTerminal'
import ScanImg from 'assets/images/scan.png'
import './CheckQueue.sass'

interface Props {
  closeHandler: () => void
}

interface IPrintCheck {
  date: string
  createdAt: string
  fromTime: string
  toTime: string
  lang: string
  qr: string
}

const CheckQueue: React.FC<Props> = ({
  closeHandler
}) => {
  const [applicationPass, setApplicationPass] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [result, setResult] = useState<IResTimeSLotApplication>()
  const [type, setType] = useState('pass')
  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const i18n = useSelector(
		(state: AppState) => state.i18n.data.components.CheckQueue,
	);
	const { lang } = useSelector((state: AppState) => state.i18n);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (type === 'pass') {
      setApplicationPass('')
      hiddenInputRef.current?.focus();
    }
  }, [type]);

  const handleWrapperClick = () => {
    if (type === 'pass') {
      hiddenInputRef.current?.focus();
    }
  };

  const handleScannerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && applicationPass.trim().length > 0) {
      setType('date');
    }
  };

  const checkQueue = async () => {
    if(!applicationPass.trim() || dateOfBirth.length !== 10){
      setHasError(true)
      return
    }
    const qr = await generateQRBase64(applicationPass)
    const params = {
      applicationPass,
      userDateOfBirth: dateOfBirth.split('.').reverse().join('-')
    }
    try {
      setLoading(true)
      const res = await timeSlotsBranchServices.getApplicationByDateAndPass(params).then((res) => res.data)
      printData({
        date: moment(res.date).format('DD.MM.YYYY'),
        createdAt: moment(res.createdAt).format('DD.MM.YYYY'),
        fromTime: res.timeslot.fromTime,
        toTime: res.timeslot.toTime,
        lang: lang,
        qr: qr,
        firstName: res?.application?.user?.firstName,
        lastName: res?.application?.user?.lastName,
        middleName: res?.application?.user?.middleName,
        applicationPass: applicationPass
      })
      setResult(res)
    } catch(e: any) {
      if(e?.response?.data?.message === 'userDateOfBirth must be a valid ISO 8601 date string'){
        ShowNotification(i18n.dateError)
        return
      }
      ShowNotification(i18n.notFound)
    } finally {
      setLoading(false)
    }
  }


  return (
    <div 
      className="check-queue"
      onClick={(e) => {
        e.stopPropagation();
        handleWrapperClick();
      }}
    >
      <CloseIcon sx={{fontSize: 25}} className='check-queue__close' onClick={closeHandler}/>
      <div className="check-queue__title">{i18n.title}</div>
      {!result && type === 'pass' && (
        <>
          <div className="check-queue__subtitle">{i18n.qrTitle} </div>
          <input
            ref={hiddenInputRef}
            value={applicationPass}
            onChange={(e) => setApplicationPass(e.target.value)}
            onKeyDown={handleScannerKeyDown}
            style={{
              position: 'absolute',
              opacity: 0,
              pointerEvents: 'none',
              top: 0,
            }}
            autoComplete="off"
          />
          <img src={ScanImg} alt="" className="check-queue__img" />
        </>
      )}
      {!result && type === 'date' && (
        <>
          <TextField
            className='check-queue__field'
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            label={i18n.dateOfBirth}
            placeholder={i18n.datePlaceholder}
            error={hasError && dateOfBirth.length !== 10}
            autoComplete='off'
            variant='outlined'
            size='small'
            InputLabelProps={{ shrink: true }}
            type='text'

          />
          <Button
            variant='contained'
            color='success'
            className='create-application-page__button'
            type='button'
            onClick={checkQueue}
            disabled={loading}
            size='large'
          >
            {i18n.save}
          </Button>
          <NumericTerminal
             value={dateOfBirth} 
             setValue={setDateOfBirth} 
             submit={checkQueue}
           />
        </>
      )}
      {!!result && (
        <>
          <div className="check-queue__row">
            <div className="check-queue__text check-queue__text--bold">{i18n.fio}: </div>
            <div className="check-queue__text">{result?.application?.user?.lastName} {result?.application?.user?.firstName} {result?.application?.user?.middleName}</div>
          </div>
          <div className="check-queue__row">
            <div className="check-queue__text check-queue__text--bold">{i18n.date}: </div>
            <div className="check-queue__text">{result?.date?.split('-').reverse().join('.')}</div>
          </div>
          <div className="check-queue__row">
            <div className="check-queue__text check-queue__text--bold">{i18n.time} </div>
            <div className="check-queue__text">{result?.timeslot?.fromTime} - {result?.timeslot?.toTime}</div>
          </div>
        </>
      )}
    </div>
  )
}

export default CheckQueue